// Client-side duplicate detection (mirrors apps/api/api/utils/duplicateTeams.ts):
// teams with the SAME unordered set of ≥2 player emails in the SAME division are
// duplicates. Maps each duplicate team → its group + the earliest-created
// survivor, so RegistrationAdmin's ⋮ menu can offer "Consolidate" only where a
// duplicate actually exists.
//
// Extracted from RegistrationAdmin.tsx so the free-agent exclusion (ADR 0054)
// is unit-testable without rendering the whole container.
import _ from 'lodash';
import helpers from '@/utils/helpers';

export const teamEmailKey = (team: any): string => {
  const emails = Array.from(new Set(
    (_.get(team, 'players', []) || [])
      .map((p: any) => String(_.get(p, 'info.email', '') || '').toLowerCase().trim())
      .filter(Boolean),
  )).sort();
  return emails.length >= 2 ? emails.join('|') : '';
};

export function buildDuplicateInfo(
  registrationTeams: any[],
): Record<string, { teams: any[]; survivorId: string }> {
  const groups: Record<string, any[]> = {};
  (registrationTeams || []).forEach((t: any) => {
    // Free agents are solo pool entries (ADR 0054): two people who happen to
    // share an email set are NOT duplicate teams, and Consolidate must never
    // merge a pool entry — exclude them from duplicate detection entirely.
    if (t.isFreeAgent) return;
    const key = teamEmailKey(t);
    if (!key) return;
    const divId = helpers.getObjId(_.get(t, 'division')) || helpers.getObjId(_.get(t, 'requestedDivision')) || '';
    const groupKey = `${divId}::${key}`;
    (groups[groupKey] = groups[groupKey] || []).push(t);
  });
  const map: Record<string, { teams: any[]; survivorId: string }> = {};
  Object.values(groups).forEach((teams) => {
    if (teams.length < 2) return;
    const survivor = _.minBy(teams, (t: any) => new Date(t.created).getTime());
    teams.forEach((t: any) => { map[String(t._id)] = { teams, survivorId: String(survivor._id) }; });
  });
  return map;
}
